import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Clock, AlertTriangle, CheckCircle2, TrendingUp } from 'lucide-react';

export default function SLAMetrics() {
  // Mock data - replace with real Supabase data
  const slaMetrics = {
    overall: {
      compliance: 87,
      total: 245,
      onTime: 213,
      breached: 32,
    },
    byType: [
      { type: 'General Support', compliance: 92, avgResponseHours: 2.3, avgResolutionHours: 18 },
      { type: 'Technical Support', compliance: 85, avgResponseHours: 1.8, avgResolutionHours: 6 },
      { type: 'Case Follow-up', compliance: 90, avgResponseHours: 20, avgResolutionHours: 65 },
      { type: 'Document Request', compliance: 78, avgResponseHours: 6, avgResolutionHours: 40 },
    ],
    nearBreach: [
      { id: '1', title: 'Ticket #1234 - Billing Inquiry', timeRemaining: '2h', type: 'billing_inquiry' },
      { id: '2', title: 'Ticket #1235 - Technical Support', timeRemaining: '4h', type: 'support_technical' },
      { id: '3', title: 'Ticket #1236 - Scheduling', timeRemaining: '6h', type: 'appointment_scheduling' },
    ],
  };

  const getComplianceColor = (compliance) => {
    if (compliance >= 90) return 'text-green-600';
    if (compliance >= 75) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getComplianceBadge = (compliance) => {
    if (compliance >= 90) return 'default';
    if (compliance >= 75) return 'secondary';
    return 'destructive';
  };

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Overall Compliance</CardTitle>
            <TrendingUp className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${getComplianceColor(slaMetrics.overall.compliance)}`}>
              {slaMetrics.overall.compliance}%
            </div>
            <Progress value={slaMetrics.overall.compliance} className="mt-2" />
            <p className="text-xs text-muted-foreground mt-2">
              {slaMetrics.overall.onTime} of {slaMetrics.overall.total} tickets
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">On Time</CardTitle>
            <CheckCircle2 className="w-4 h-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {slaMetrics.overall.onTime}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Tickets resolved on time
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Overdue</CardTitle>
            <AlertTriangle className="w-4 h-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {slaMetrics.overall.breached}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Tickets that breached SLA
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Near Deadline</CardTitle>
            <Clock className="w-4 h-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {slaMetrics.nearBreach.length}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Tickets need attention
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Performance por Tipo */}
      <Card>
        <CardHeader>
          <CardTitle>Performance by Ticket Type</CardTitle>
          <CardDescription>SLA metrics by category</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {slaMetrics.byType.map((type, index) => (
              <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="font-medium">{type.type}</h4>
                    <Badge variant={getComplianceBadge(type.compliance)}>
                      {type.compliance}% compliance
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
                    <div>
                      <span className="font-medium">Avg Response Time:</span> {type.avgResponseHours}h
                    </div>
                    <div>
                      <span className="font-medium">Avg Resolution Time:</span> {type.avgResolutionHours}h
                    </div>
                  </div>
                </div>
                <Progress value={type.compliance} className="w-32" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Tickets Próximos ao Prazo */}
      <Card>
        <CardHeader>
          <CardTitle>Tickets Requiring Attention</CardTitle>
          <CardDescription>Tickets close to breaching SLA</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {slaMetrics.nearBreach.map((ticket) => (
              <div key={ticket.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 transition-colors">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                  <div>
                    <p className="font-medium">{ticket.title}</p>
                    <p className="text-sm text-muted-foreground">
                      Time remaining: {ticket.timeRemaining}
                    </p>
                  </div>
                </div>
                <Badge variant="outline" className="ml-4">
                  {ticket.type.replace(/_/g, ' ')}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
